/// <amd-module name="Environments/Providers/VirtualMachineGroups/Services/VirtualMachineGroupServices" />
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { PageContextHelper } from "Environments/Client/PageContextHelper";
import { getEnvironmentsClient, IEnvironmentsRestClient } from "Environments/Client/RestClient/Environments";
import { getTaskAgentClient, ITaskAgentRestClient } from "DistributedTask/Client/RestClient/TaskAgent";
import { IVirtualMachineGroupsExtendedClient, IVirtualMachineGroupTargetsResult } from "Environments/Providers/VirtualMachineGroups/Services/VirtualMachineGroupExtendedClient";

import {
    VirtualMachineGroup,
    PackageMetadata,
    TaskAgentPool,
    VirtualMachine
} from "DistributedTask/Client/Contracts/TaskAgent";

export interface IVirtualMachineGroupService extends IVssService {
    getVirtualMachineGroupById(environmentId: number, resourceId: number): Promise<VirtualMachineGroup>;
    getVirtualMachines(environmentId: number, resourceId: number, continuationToken?: string, top?: number): Promise<IVirtualMachineGroupTargetsResult>;
    getTargetPackages(): Promise<PackageMetadata[]>;
    getTaskAgentPool(poolId: number): Promise<TaskAgentPool>;
    getPATForDeploymentPool(poolId: number): Promise<string>;
    updateVirtualMachines(environmentId: number, resourceId: number, virtualMachines: VirtualMachine[]): Promise<VirtualMachine[]>;
}

class VirtualMachineGroupsServices extends VssService implements IVirtualMachineGroupService {
    public getVirtualMachineGroupById(environmentId: number, resourceId: number): Promise<VirtualMachineGroup> {
        return this._getClient().getVirtualMachineGroup(this._getProjectName(), environmentId, resourceId);
    }

    public getVirtualMachines(environmentId: number, resourceId: number, continuationToken?: string, top?: number): Promise<IVirtualMachineGroupTargetsResult> {
        return this.pageContext
            .getRestClient<IVirtualMachineGroupsExtendedClient>("IVirtualMachineGroupsExtendedClient")
            .getVirtualMachineGroupTargetsWithContinuationToken(this._getProjectName(), environmentId, resourceId, continuationToken, top)

    }

    public updateVirtualMachines(environmentId: number, resourceId: number, virtualMachines: VirtualMachine[]): Promise<VirtualMachine[]> {
        return this._getClient().updateVirtualMachines(virtualMachines, this._getProjectName(), environmentId, resourceId);
    }

    public getTargetPackages(): Promise<PackageMetadata[]> {
        return this._getTaskAgentClient().getPackages("agent", undefined, 1);
    }

    public getTaskAgentPool(poolId: number): Promise<TaskAgentPool> {
        return this._getTaskAgentClient().getAgentPool(poolId, undefined, undefined);
    }

    public getPATForDeploymentPool(poolId: number): Promise<string> {
        return this._getTaskAgentClient().generateDeploymentPoolAccessToken(poolId);
    }

    private _getProjectName(): string {
        const projectName = PageContextHelper.getProjectName(this.pageContext);
        if (!!projectName) {
            return projectName;
        }

        throw new Error("Release rest client :: Project is not available in page data. Cannot make this network call without project information");
    }

    private _getTaskAgentClient(): ITaskAgentRestClient {
        return getTaskAgentClient(this.pageContext);
    }

    private _getEnvironmentClient(): IEnvironmentsRestClient {
        return getEnvironmentsClient(this.pageContext);
    }

    private _getClient(): ITaskAgentRestClient | IEnvironmentsRestClient {
        return getTaskAgentClient(this.pageContext);
    }
}

Services.add("IVirtualMachineGroupService", { serviceFactory: VirtualMachineGroupsServices });
